import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button } from '../../../components/_base/Button';
import API, { MeetingSlot, PROTECTED, SpeakerProfile, PrivateMeetingDetailsResponseType } from '../../../lib/api';
import { getCurrentTimezone, dateToFormattedString, DateFnsFormats, formatTimeWithUppercasePeriod, shouldShowLocalTime } from '../../../lib/helpers/dateHelper';
import { useBookingSubscription, RegistrationResult, RegistrationResultType } from '../../../hooks/use-booking-subscription';
import { BOOKING_MESSAGES, BOOKING_BUTTON_LABELS, ModalMode } from '../../../lib/constants';
import MeetingDetailsSection from './MeetingDetailsSection';
import './BookMeetingModal.scss';

const { bookMeeting, cancelBooking, initiateBooking } = API[PROTECTED];


interface BookMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: RegistrationResult) => void;
  slot: MeetingSlot | null;
  speakers?: SpeakerProfile[];
  eventTimeZone: string;
  eventId?: string;
  meetingId?: string;
  mode?: ModalMode;
  meetingDetails?: PrivateMeetingDetailsResponseType;
  transformedTimeZone?: string;
}

const BookMeetingModal: React.FC<BookMeetingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  slot,
  speakers = [],
  eventTimeZone,
  eventId,
  meetingId,
  mode = ModalMode.BOOK,
  meetingDetails,
  transformedTimeZone = ''
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const hasHandledResult = useRef(false);
  
  const onSubscriptionStart = useCallback(() => {
    setIsLoading(true);
    hasHandledResult.current = false;
  }, []);

  const { state: subscriptionState, subscribe } = useBookingSubscription(onSubscriptionStart);

  const isCancelMode = mode === ModalMode.CANCEL;
  const currentTimeZone = getCurrentTimezone();
  
  const cancelModeSpeakers = useMemo(() => {
    if (!isCancelMode || !meetingDetails) return [];
    
    return [
      ...(meetingDetails.presenterOrganizations || []),
      ...(meetingDetails.participantOrganizations || [])
    ].flatMap(org => 
      (org.attendees || []).map(attendee => ({
        id: attendee.attendeeId.toString(),
        name: `${attendee.firstName || ''} ${attendee.lastName || ''}`.trim(),
        title: attendee.title || '',
        organization: org.name,
        profileImageUrl: attendee.profileImageUrl || '',
        biography: attendee.biography || ''
      }))
    );
  }, [isCancelMode, meetingDetails]);

  useEffect(() => {
    if (hasHandledResult.current) return;
    
    if (subscriptionState.result !== null) {
      hasHandledResult.current = true;
      setIsLoading(false);
      onConfirm(subscriptionState.result);
    }
    if (subscriptionState.error) {
      hasHandledResult.current = true;
      setIsLoading(false);
      onConfirm(RegistrationResultType.FAILED);
    }
  }, [subscriptionState.result, subscriptionState.error, onConfirm]);
  
  if (!isOpen || !eventId) {
    return null;
  }

  const isBookModeInvalid = !isCancelMode && (!slot || !speakers || speakers.length === 0);
  const isCancelModeInvalid = isCancelMode && !meetingId;

  if (isBookModeInvalid || isCancelModeInvalid) {
    return null;
  }

  const effectiveSpeakers = isCancelMode ? cancelModeSpeakers : speakers;

  const meetingStartDateTime = slot?.startDateTime || meetingDetails?.startDateTime;
  const meetingEndDateTime = slot?.endDateTime || meetingDetails?.endDateTime;
  const startTimeEvent = meetingStartDateTime ? formatTimeWithUppercasePeriod(meetingStartDateTime, eventTimeZone) : '';
  const endTimeEvent = meetingEndDateTime ? formatTimeWithUppercasePeriod(meetingEndDateTime, eventTimeZone) : '';
  const startTimeLocal = meetingStartDateTime ? formatTimeWithUppercasePeriod(meetingStartDateTime, currentTimeZone) : '';
  const endTimeLocal = meetingEndDateTime ? formatTimeWithUppercasePeriod(meetingEndDateTime, currentTimeZone) : '';
  const date = meetingStartDateTime 
    ? dateToFormattedString(meetingStartDateTime, DateFnsFormats.StringifiedDayMonthNumericDateYear, eventTimeZone)
    : '';
  const displayLocalTime = meetingStartDateTime
    ? shouldShowLocalTime(meetingStartDateTime, eventTimeZone)
    : false;

  const handleConfirm = async () => {
    setIsLoading(true);
    hasHandledResult.current = false;
    try {
      const response = isCancelMode && meetingId
        ? await cancelBooking(eventId, meetingId)
        : slot
          ? await bookMeeting(eventId, slot.id)
          : null;
      
      if (!response) {
        onConfirm(RegistrationResultType.FAILED);
        return;
      }
      
      await subscribe({ registrationId: response.registrationId, appSyncToken: response.appSyncToken });
      await initiateBooking(eventId, response.registrationId);
    } catch (error) {
      if (!hasHandledResult.current) {
        hasHandledResult.current = true;
        setIsLoading(false);
        const status = (error as { status?: number })?.status;
        const result = isCancelMode
          ? RegistrationResultType.FAILED
          : (status === 400 ? RegistrationResultType.UNAVAILABLE : RegistrationResultType.FAILED);
        onConfirm(result);
      }
    }
  };

  return (
    <div className="book-meeting-modal-overlay" role="dialog" aria-modal="true">
      <div className="book-meeting-modal">
        <div className="modal-content">
          <div className="modal-header-content">
            <p className="modal-header">
              {isCancelMode 
                ? <>{BOOKING_MESSAGES.CANCELLATION_CONFIRM} <strong>{BOOKING_MESSAGES.CANCELLATION_CONFIRM_BOLD}</strong> {BOOKING_MESSAGES.CANCELLATION_CONFIRM_SUFFIX}</>
                : BOOKING_MESSAGES.BOOKING_CONFIRM}
            </p>
            <MeetingDetailsSection
              speakers={effectiveSpeakers}
              date={date}
              startTimeEvent={startTimeEvent}
              endTimeEvent={endTimeEvent}
              transformedTimeZone={transformedTimeZone}
              showLocalTime={displayLocalTime}
              startTimeLocal={startTimeLocal}
              endTimeLocal={endTimeLocal}
              isLoading={isLoading}
              loadingMessage={isCancelMode ? BOOKING_MESSAGES.LOADING_CANCELLATION : BOOKING_MESSAGES.LOADING_BOOKING}
            />
          </div>
          <div className="modal-actions">
            <Button
              onClick={onClose}
              className={isCancelMode ? "keep-booking-btn" : "cancel-btn"}
              label={isCancelMode ? BOOKING_BUTTON_LABELS.KEEP_BOOKING : BOOKING_BUTTON_LABELS.CANCEL}
              name="cancel-meeting"
              type="button"
              disabled={isLoading}
            />
            <Button
              onClick={handleConfirm}
              className={isCancelMode ? "cancel-booking-confirm-btn" : "confirm-btn"}
              label={isCancelMode ? BOOKING_BUTTON_LABELS.CONFIRM_CANCELLATION : BOOKING_BUTTON_LABELS.CONFIRM}
              name="confirm-meeting"
              type="button"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookMeetingModal;
